import { type ElementRef, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AudioPlayButton, AudioTag, AudioWave } from 'components-ref/atoms'
import {
    AudioContextProvider,
    useAudioStore
} from 'context/AudioContext/AudioContext'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import colors from 'styles/colors'
import { type IPostFile } from 'types/interfaces/ITypes'

interface IAudioMinimizedProps {
    mediaUrl: string
    postFile: IPostFile
}

interface IAudioMinimizedPostProps {
    postFile: IPostFile
}

export const AudioMinimized = ({
    mediaUrl,
    postFile
}: IAudioMinimizedProps) => {
    return (
        <AudioContextProvider audioReady={true} mediaUrl={mediaUrl}>
            <AudioMinimizedPost postFile={postFile} />
        </AudioContextProvider>
    )
}

const AudioMinimizedPost = ({ postFile }: IAudioMinimizedPostProps) => {
    const { t } = useTranslation()
    const { setPlaying } = useAudioStore()
    const audioRef = useRef<ElementRef<'audio'>>(null)

    useEffect(() => {
        const listener = emitter.addListener(
            EventsEmitter.STOP_ALL_AUDIO_POSTS,
            () => {
                if (audioRef?.current) {
                    audioRef?.current?.pause()
                }

                setPlaying(false)
            }
        )

        return () => {
            listener.remove()
        }
    }, [])

    return (
        <>
            <AudioTag ref={audioRef} />
            <Wrapper>
                <AudioWaveContainer>
                    <AudioWaveWrapper>
                        <AudioWave
                            waveColor='gray'
                            waveBackgroundColor='transparent'
                            audioRef={audioRef?.current}
                        />
                    </AudioWaveWrapper>
                </AudioWaveContainer>
                <PreviewMinimizedContent>
                    <PreviewMinimizedTop>
                        <HeaderAvatar>
                            <img
                                src={postFile.minimizedAvatar}
                                alt={t('placeholder')}
                            />
                        </HeaderAvatar>
                        <HeaderName>{postFile.minimizedName}</HeaderName>
                    </PreviewMinimizedTop>
                    <div>
                        <ContentText>{postFile.text}</ContentText>
                    </div>
                </PreviewMinimizedContent>
                <PlayButtonContainer>
                    <AudioPlayButton audioRef={audioRef} />
                </PlayButtonContainer>
            </Wrapper>
        </>
    )
}

const PlayButtonContainer = styled.div`
    flex: 1;
    padding-right: 35px;
    flex-shrink: 0;
`

const Wrapper = styled.div`
    width: 100%;
    height: 8rem;
    overflow: hidden;
    background: white;
    display: flex;
    align-items: center;
`

const AudioWaveContainer = styled.div`
    width: 142px;
    height: 100%;
    background: #edf2f8;
    border-radius: 1rem;
    position: relative;
    flex-shrink: 0;
`

const AudioWaveWrapper = styled.div`
    background: white;
    height: 50%;
    width: 80%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 1rem;
    display: flex;
    padding: 1rem;
`

const PreviewMinimizedContent = styled.div`
    padding-left: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
`

const PreviewMinimizedTop = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0.3rem;
`
const ContentText = styled.div`
    color: ${colors.blueDark};
`

const HeaderAvatar = styled.div`
    width: 2rem;
    height: 2rem;
    border-radius: 3rem;
    overflow: hidden;
    margin-right: 0.5rem;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

const HeaderName = styled.div`
    color: ${colors.blueDark};
    font-size: 1.2rem;
    line-height: 1.5rem;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.02em;
`
